import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Alert, Stack, TextField, Button, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import {
  getScteAudiences, getScteVps, getScteMediapoints,
  upsertScteAudience, deleteScteAudience,
  upsertScteVp, deleteScteVp,
  upsertScteMediapoint, deleteScteMediapoint,
  ScteAudience, ScteVp, ScteMp,
} from "../../api";

const mono = { fontFamily: "monospace", fontSize: 12 } as const;
const DEFAULT_NS = "urn:scte:224:action";

// Actions <-> textarea: one action per line, "namespace action" (space-separated)
// or just "action" (namespace defaults to urn:scte:224:action).
function parseActions(text: string): { namespace: string; action: string }[] {
  return text.split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
    const sp = line.indexOf(" ");
    if (sp > 0) return { namespace: line.slice(0, sp).trim(), action: line.slice(sp + 1).trim() };
    return { namespace: DEFAULT_NS, action: line };
  });
}
function actionsToText(actions: { namespace: string; action: string }[]): string {
  return actions.map((a) => (a.namespace === DEFAULT_NS ? a.action : `${a.namespace} ${a.action}`)).join("\n");
}

// ----------------------------- Audiences -----------------------------

function AudiencesTab() {
  const [rows, setRows] = useState<ScteAudience[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ScteAudience | null>(null);
  const [form, setForm] = useState({ id: "", name: "", match: "", criteria: "", parentId: "" });

  const load = useCallback(() => {
    getScteAudiences().then(setRows).catch((e) => setErr(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ id: "", name: "", match: "", criteria: "", parentId: "" }); setErr(null); setOpen(true); };
  const openEdit = (a: ScteAudience) => {
    setEditing(a);
    setForm({ id: a.id, name: a.name, match: a.match || "", criteria: a.criteria || "", parentId: a.parentId || "" });
    setErr(null); setOpen(true);
  };
  const save = async () => {
    try {
      await upsertScteAudience({
        id: editing ? editing.id : (form.id || undefined),
        name: form.name,
        match: form.match || null,
        criteria: form.criteria || null,
        parentId: form.parentId || null,
      }, !!editing);
      setOpen(false); load();
    } catch (e: any) { setErr(e.message); }
  };
  const remove = async (a: ScteAudience) => {
    if (!window.confirm(`Delete audience ${a.id}?`)) return;
    try { await deleteScteAudience(a.id); load(); } catch (e: any) { setErr(e.message); }
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ opacity: 0.6 }}>{rows.length} Audiences</Typography>
        <Button size="small" variant="contained" startIcon={<Add />} onClick={openNew}>New Audience</Button>
      </Stack>
      {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}
      <Table size="small">
        <TableHead><TableRow>
          <TableCell>id</TableCell><TableCell>name</TableCell><TableCell>match</TableCell>
          <TableCell>criteria</TableCell><TableCell align="right">actions</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {rows.map((a) => (
            <TableRow key={a.id}>
              <TableCell sx={mono}>{a.id}</TableCell>
              <TableCell>{a.name}</TableCell>
              <TableCell>{a.match || "—"}</TableCell>
              <TableCell sx={mono}>{a.criteria}</TableCell>
              <TableCell align="right">
                <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(a)}><Edit fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Delete"><IconButton size="small" onClick={() => remove(a)}><Delete fontSize="small" /></IconButton></Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Edit Audience" : "New Audience"}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField label="id" size="small" value={editing ? editing.id : form.id}
              onChange={(e) => setForm({ ...form, id: e.target.value })}
              disabled={!!editing} helperText={editing ? "id is immutable" : "optional; derived from name if blank"} />
            <TextField label="name" size="small" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <TextField label="match (ANY / ALL / NONE)" size="small" value={form.match} onChange={(e) => setForm({ ...form, match: e.target.value })} />
            <TextField label="criteria (JSON)" size="small" value={form.criteria} onChange={(e) => setForm({ ...form, criteria: e.target.value })}
              helperText='e.g. {"token":"comcast"} (defaults from id)' />
            <TextField label="parentId" size="small" value={form.parentId} onChange={(e) => setForm({ ...form, parentId: e.target.value })}
              helperText="optional; an existing audience id (nested-audience parent)" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!form.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// -------------------------- ViewingPolicies --------------------------

function VpsTab() {
  const [rows, setRows] = useState<ScteVp[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ScteVp | null>(null);
  const [form, setForm] = useState({ id: "", audienceId: "", description: "", actions: "" });

  const load = useCallback(() => { getScteVps().then(setRows).catch((e) => setErr(e.message)); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ id: "", audienceId: "", description: "", actions: "" }); setErr(null); setOpen(true); };
  const openEdit = (v: ScteVp) => {
    setEditing(v);
    setForm({ id: v.id, audienceId: v.audienceId || "", description: v.description || "", actions: actionsToText(v.actions) });
    setErr(null); setOpen(true);
  };
  const save = async () => {
    try {
      await upsertScteVp({
        id: editing ? editing.id : undefined,
        audienceId: form.audienceId || null,
        description: form.description || null,
        actions: parseActions(form.actions),
      }, !!editing);
      setOpen(false); load();
    } catch (e: any) { setErr(e.message); }
  };
  const remove = async (v: ScteVp) => {
    if (!window.confirm(`Delete viewing policy ${v.id}?`)) return;
    try { await deleteScteVp(v.id); load(); } catch (e: any) { setErr(e.message); }
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ opacity: 0.6 }}>{rows.length} ViewingPolicies</Typography>
        <Button size="small" variant="contained" startIcon={<Add />} onClick={openNew}>New ViewingPolicy</Button>
      </Stack>
      {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}
      <Table size="small">
        <TableHead><TableRow>
          <TableCell>id</TableCell><TableCell>audience</TableCell><TableCell>actions</TableCell>
          <TableCell align="right">edit</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {rows.map((v) => (
            <TableRow key={v.id}>
              <TableCell sx={mono}>{v.id}</TableCell>
              <TableCell sx={mono}>{v.audienceId}</TableCell>
              <TableCell>{v.actions.map((a, i) => <Chip key={i} size="small" variant="outlined" sx={{ mr: 0.5 }} label={a.action} />)}</TableCell>
              <TableCell align="right">
                <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(v)}><Edit fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Delete"><IconButton size="small" onClick={() => remove(v)}><Delete fontSize="small" /></IconButton></Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Edit ViewingPolicy" : "New ViewingPolicy"}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            {editing && <TextField label="id" size="small" value={editing.id} disabled />}
            <TextField label="audienceId" size="small" value={form.audienceId} onChange={(e) => setForm({ ...form, audienceId: e.target.value })}
              helperText="an existing audience id, e.g. /audience/comcast" />
            <TextField label="description" size="small" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <TextField label="actions (one per line)" size="small" multiline minRows={3} value={form.actions}
              onChange={(e) => setForm({ ...form, actions: e.target.value })}
              helperText="each line: 'action' or 'namespace action' (default ns urn:scte:224:action)" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ---------------------------- MediaPoints ----------------------------

function MediaPointsTab() {
  const [channel, setChannel] = useState("");
  const [rows, setRows] = useState<ScteMp[]>([]);
  const [size, setSize] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ScteMp | null>(null);
  const [form, setForm] = useState({ id: "", channel: "", matchTime: "", duration: "", policyId: "", priority: "", tolerance: "", asserts: "" });

  const load = useCallback(async () => {
    setErr(null);
    try {
      const res = await getScteMediapoints({ channel: channel || undefined, limit: 300 });
      setRows(res.items); setSize(res.size);
    } catch (e: any) { setErr(e.message); }
  }, [channel]);
  useEffect(() => { load(); }, []); // initial

  const openNew = () => {
    setEditing(null);
    setForm({ id: "", channel: channel || "", matchTime: "", duration: "PT1H", policyId: "", priority: "", tolerance: "", asserts: "" });
    setErr(null); setOpen(true);
  };
  const openEdit = (m: ScteMp) => {
    setEditing(m);
    setForm({ id: m.id, channel: m.channel, matchTime: m.matchTime || "", duration: m.duration || "", policyId: m.policy, priority: "", tolerance: "", asserts: "" });
    setErr(null); setOpen(true);
  };
  const save = async () => {
    try {
      const asserts = form.asserts.split("\n").map((l) => l.trim()).filter(Boolean);
      const hasSignal = asserts.length > 0 || form.tolerance.trim() !== "";
      await upsertScteMediapoint({
        id: editing ? editing.id : undefined,
        channel: form.channel,
        matchTime: form.matchTime,
        duration: form.duration || undefined,
        policyId: form.policyId,
        priority: form.priority ? Number(form.priority) : undefined,
        matchSignal: hasSignal ? { match: "ALL", tolerance: form.tolerance || undefined, asserts } : undefined,
      }, !!editing);
      setOpen(false); load();
    } catch (e: any) { setErr(e.message); }
  };
  const remove = async (m: ScteMp) => {
    if (!window.confirm(`Delete mediapoint ${m.id}?`)) return;
    try { await deleteScteMediapoint(m.id); load(); } catch (e: any) { setErr(e.message); }
  };

  return (
    <>
      <Stack direction="row" gap={1} sx={{ mb: 1 }} alignItems="center" justifyContent="space-between">
        <Stack direction="row" gap={1} alignItems="center">
          <TextField size="small" label="channel filter" value={channel} onChange={(e) => setChannel(e.target.value)} />
          <Button variant="outlined" size="small" onClick={load}>Filter</Button>
          <Typography variant="caption" sx={{ opacity: 0.6 }}>size {size} (showing {rows.length}, newest first)</Typography>
        </Stack>
        <Button size="small" variant="contained" startIcon={<Add />} onClick={openNew}>New MediaPoint</Button>
      </Stack>
      {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}
      <Table size="small">
        <TableHead><TableRow>
          <TableCell>id</TableCell><TableCell>channel</TableCell><TableCell>matchTime</TableCell>
          <TableCell>duration</TableCell><TableCell>policy</TableCell><TableCell>signal</TableCell>
          <TableCell>audiences</TableCell><TableCell align="right">edit</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {rows.map((m) => (
            <TableRow key={m.id}>
              <TableCell sx={mono}>{m.id}</TableCell>
              <TableCell>{m.channel}</TableCell>
              <TableCell>{m.matchTime}</TableCell>
              <TableCell>{m.duration}</TableCell>
              <TableCell sx={mono}>{m.policy}</TableCell>
              <TableCell>{m.hasSignal ? <Chip size="small" color="secondary" label="SIGNAL" /> : "—"}</TableCell>
              <TableCell>{m.audiences.join(", ")}</TableCell>
              <TableCell align="right">
                <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(m)}><Edit fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Delete"><IconButton size="small" onClick={() => remove(m)}><Delete fontSize="small" /></IconButton></Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Edit MediaPoint" : "New MediaPoint"}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            {editing && <TextField label="id" size="small" value={editing.id} disabled />}
            <TextField label="channel" size="small" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} required />
            <TextField label="matchTime (ISO-8601)" size="small" value={form.matchTime} onChange={(e) => setForm({ ...form, matchTime: e.target.value })}
              required helperText="e.g. 2026-04-01T12:00:00Z" />
            <TextField label="duration" size="small" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} helperText="e.g. PT1H (default)" />
            <TextField label="policyId" size="small" value={form.policyId} onChange={(e) => setForm({ ...form, policyId: e.target.value })}
              required helperText="an existing policy id (see the policy column)" />
            <TextField label="priority" size="small" type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
              helperText="optional; lower = higher precedence" />
            <Typography variant="caption" sx={{ opacity: 0.6 }}>Optional MatchSignal (gates the apply; falls back to time after tolerance)</Typography>
            <TextField label="signalTolerance" size="small" value={form.tolerance} onChange={(e) => setForm({ ...form, tolerance: e.target.value })}
              helperText="e.g. PT30S; leave blank for none" />
            <TextField label="asserts (one XPath per line)" size="small" multiline minRows={2} value={form.asserts}
              onChange={(e) => setForm({ ...form, asserts: e.target.value })}
              helperText="e.g. //SegmentationUpid[@segmentationUpidType=8]" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!form.channel.trim() || !form.matchTime.trim() || !form.policyId.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function ScteGraph() {
  const [tab, setTab] = useState(0);
  return (
    <Box sx={{ maxWidth: 1150, mx: "auto" }}>
      <Typography variant="h5" gutterBottom>SCTE-224 Graph</Typography>
      <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
        The managed-resource graph behind the decision engine: Audiences, ViewingPolicies (Audience x Action),
        and MediaPoints (each applying its Policy, optionally signal-gated). Create / edit / delete here, via the
        EPG, or via the external SCTE-224 XML interface. Audiences and ViewingPolicies are super-admin; MediaPoints
        follow channel write permissions.
      </Typography>
      <Paper sx={{ p: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab label="Audiences" /><Tab label="ViewingPolicies" /><Tab label="MediaPoints" />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {tab === 0 && <AudiencesTab />}
          {tab === 1 && <VpsTab />}
          {tab === 2 && <MediaPointsTab />}
        </Box>
      </Paper>
    </Box>
  );
}
